import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bubbles } from "@/components/Motifs";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success("Password updated! You can now sign in.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-navy relative overflow-hidden px-6">
      <Bubbles count={16} dark />
      <div className="relative z-10 w-full max-w-md rounded-3xl bg-cream p-10 shadow-2xl">
        <img src="/assets/logo.png" alt="Float Now" className="h-16 w-16 object-contain mx-auto mb-3" />
        <div className="flex items-center justify-center gap-2 text-teal mb-1"><Waves size={18} /><span className="text-xs tracking-[0.2em] uppercase font-semibold">Float Now</span></div>
        {!token ? (
          <div className="text-center">
            <h1 className="font-serif text-2xl text-navy mt-2">Invalid reset link</h1>
            <p className="mt-2 text-navy/60 text-sm">This link is missing or broken. Please request a new one from the sign-in page.</p>
            <Button asChild className="mt-5 rounded-full bg-coral text-white"><Link to="/signin">Back to Sign In</Link></Button>
          </div>
        ) : done ? (
          <div className="text-center">
            <h1 className="font-serif text-2xl text-navy mt-2">All set!</h1>
            <p className="mt-2 text-navy/60 text-sm">Your password has been updated.</p>
            <Button onClick={() => navigate("/signin")} data-testid="reset-gotosignin" className="mt-5 rounded-full bg-coral hover:bg-[#C95B50] text-white">Sign In</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4">
            <h1 className="font-serif text-2xl text-navy text-center mb-5">Choose a new password</h1>
            <div className="space-y-4">
              <div><Label>New password</Label><Input type="password" required minLength={6} data-testid="reset-password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 bg-white" /></div>
              <div><Label>Confirm password</Label><Input type="password" required data-testid="reset-password-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1 bg-white" /></div>
              <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full rounded-full bg-coral hover:bg-[#C95B50] text-white">{loading ? "Updating…" : "Update Password"}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
